import { FACEBOOK_PIXEL_ID, USER_KEY, GRAND_TOTAL_KEY } from "../Constant";

/* ------------------ Helpers ------------------ */

const safeFbq = (...args) => {
    if (typeof window.fbq === "function") {
        window.fbq(...args);
    }
};

const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
};

const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
};

const generateEventId = (prefix = "evt") => {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const normalizeEmail = (email) => {
    return email?.trim().toLowerCase();
};

const normalizePhone = (phone) => {
    return phone?.replace(/\D/g, "");
};

const FBC_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days in milliseconds

/**
 * Extract fbclid from the current URL and set/refresh the _fbc cookie.
 * Per Meta docs, only update when:
 *  - _fbc cookie doesn't exist and fbclid is in URL, OR
 *  - fbclid in URL differs from the fbclid stored in the existing _fbc cookie
 * Format: fb.1.<creationTime>.<fbclid>
 */
export const refreshFbcCookie = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get("fbclid");

        if (!fbclid) return;

        const existingFbc = getCookie("_fbc");

        if (existingFbc) {
            // Extract the fbclid portion (everything after the last dot)
            const existingFbclid = existingFbc.substring(existingFbc.lastIndexOf(".") + 1);
            if (existingFbclid === fbclid) return; // same click, no update needed
        }

        // Format and set the _fbc cookie with 90 day expiry
        const formattedFbc = `fb.1.${Date.now()}.${fbclid}`;
        setCookie("_fbc", formattedFbc, 90);
    } catch (e) {
        // silently ignore errors in cookie handling
    }
};

/**
 * Get a validated _fbc value. Returns null if the cookie is stale (older than 90 days).
 */
const getValidFbc = () => {
    const fbc = getCookie("_fbc");
    if (!fbc) return null;

    try {
        // Format: fb.1.<creationTime>.<fbclid>
        const parts = fbc.split(".");
        if (parts.length < 4) return null;

        const creationTime = parseInt(parts[2], 10);
        if (isNaN(creationTime)) return null;

        // Discard if older than 90 days
        if (Date.now() - creationTime > FBC_MAX_AGE_MS) return null;
    } catch (e) {
        return null;
    }

    return fbc;
};

export const initPixel = (user) => {
    const userData = {};
    if (user?.email) {
        userData.em = normalizeEmail(user.email);
    }
    if (user?.first_name) {
        userData.fn = user.first_name.trim().toLowerCase();
    }
    if (user?.mobile_number) {
        userData.ph = normalizePhone(user.mobile_number);
    }
    if (user?.customer_id) {
        userData.external_id = user.customer_id.toString();
    } 
    else{
        userData.external_id = localStorage.getItem('token') || "";
    }

    safeFbq("init", FACEBOOK_PIXEL_ID, userData);
};

const baseUserParams = (user) => {
    const params = {
        fbp: getCookie("_fbp"),
        fbc: getValidFbc()
    };
    if (user?.customer_id) {
        params.external_id = user.customer_id.toString();
    }
        else {
        params.external_id = localStorage.getItem('token') || "";
    }
    return params;
};

/* ------------------ Checkout ------------------ */

export const checkoutEventsFB = (data) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    const products = data?.products?.map(product => ({
        id: product.sku,
        quantity: 1,
        item_price: parseFloat(product.price || 0)
    })) || [];

    let totalValue = data?.totals?.find(x => x.name === GRAND_TOTAL_KEY)?.value || "0";
    totalValue = parseFloat(totalValue.replace("$", "").replace(/,/g, ""));

    safeFbq("track", "PageView", {
    ...baseUserParams(user),
    customer_Ids: user?.customer_id ? [user.customer_id] : [],
    membership_type: user ? "Yes" : "Guest user",
    total_order: user ? user.total_order : 0,
    value: totalValue,
    currency: "USD",
    event_id: data?.pageview_event_id || generateEventId("page_view")
    });

    safeFbq("track", "InitiateCheckout", {
        ...baseUserParams(user),
        contents: products,
        content_type: "product",
        value: totalValue,
        currency: "USD",
        event_id: data?.initiatecheckout_event_id || generateEventId("initiate_checkout")
    });

    return data?.initiatecheckout_event_id || generateEventId("initiate_checkout"); // send to backend for CAPI dedup
};

/* ------------------ Product Page ------------------ */

export const productPageEventsFB = (data) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    const sku = data?.product?.product_info?.[0]?.value;
    let totalValue = parseFloat(
        (data?.product?.discount_price || "0").replace("$", "").replace(/,/g, "")
    );
    
    safeFbq("track", "PageView", {
    ...baseUserParams(user),
    customer_Ids: user?.customer_id ? [user.customer_id] : [],
    membership_type: user ? "Yes" : "Guest user",
    total_order: user ? user.total_order : 0,
    value: totalValue,
    currency: "USD",
    event_id: data?.pageview_event_id || generateEventId("page_view")
    });

    safeFbq("track", "ViewContent", {
        ...baseUserParams(user),
        content_ids: [sku],
        content_type: "product",
        value: totalValue,
        currency: "USD",
        event_id: data?.pageview_event_id || generateEventId("view_content")
    });

        // adroll code
    try {
        if (user) {
            window.adroll.track('productView', { "products": [{ "product_id": sku[0]?.value }] });
            window.__adroll.record_user(window.adroll_custom_data = { "customer_type": "Yes", "customer_id": user?.customer_id, "telephone": user.mobile_number ? user.mobile_number : "", "page_type": "Product" });
        } else {
            window.adroll.track('productView', { "products": [{ "product_id": sku[0]?.value }] });
            window.__adroll.record_user(window.adroll_custom_data = { "customer_type": "Guest user", "page_type": "Product" });
        }
    } catch (e) {
        // adroll not loaded
    }

    return data?.pageview_event_id || generateEventId("view_content");
};

/* ------------------ Add To Cart ------------------ */

export const addToCartEventsFB = (product, cartProduct, event_id) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    const productDetails =
        product?.options?.[0]?.items?.find(x => x.id === cartProduct?.value);

    const sku = productDetails?.sku || product?.product_info?.[0]?.value;

    let totalValue = parseFloat(
        (product?.discount_price || "0").replace("$", "").replace(/,/g, "")
    );

    safeFbq("track", "AddToCart", {
        ...baseUserParams(user),
        content_ids: [sku],
        content_type: "product",
        value: totalValue,
        currency: "USD",
        event_id: event_id || generateEventId("add_to_cart")
    });

    return event_id || generateEventId("add_to_cart");
};

/* ------------------ Wishlist ------------------ */

export const addToWishlistEventsFB = (data) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    const contents = data?.wishlist_products?.map(product => ({
        id: product.product_id,
        quantity: 1,
        item_price: parseFloat(
            (product.discount_price || "0").replace("$", "").replace(/,/g, "")
        )
    })) || [];

    safeFbq("track", "PageView", {
    ...baseUserParams(user),
    customer_Ids: user?.customer_id ? [user.customer_id] : [],
    membership_type: user ? "Yes" : "Guest user",
    total_order: user ? user.total_order : 0,
    value: contents.reduce((sum, item) => sum + (item.item_price || 0), 0),
    currency: "USD",
    event_id: data?.pageview_event_id || generateEventId("page_view")
    });

    safeFbq("track", "AddToWishlist", {
        ...baseUserParams(user),
        contents: contents,
        content_type: "product",
        currency: "USD",
        event_id: data?.addtowishlist_event_id || generateEventId("add_to_wishlist")
    });

    return data?.addtowishlist_event_id || generateEventId("add_to_wishlist");
};

/* ------------------ Category / Search ------------------ */

export const categoryPageEventsFB = (data, searchTerm) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    safeFbq("track", "PageView", {
    ...baseUserParams(user),
    customer_Ids: user?.customer_id ? [user.customer_id] : [],
    membership_type: user ? "Yes" : "Guest user",
    total_order: user ? user.total_order : 0,
    currency: "USD",
    event_id: data?.pageview_event_id || generateEventId("page_view")
    });

    if (searchTerm) {
        safeFbq("track", "Search", {
            ...baseUserParams(user),
            search_string: searchTerm,
            event_id: data?.search_event_id || generateEventId("search")
        });
    } else {
        safeFbq("trackCustom", "ViewCategory", {
            ...baseUserParams(user),
            content_category: data?.fproducts?.category_name || "",
            event_id: data?.viewcategory_event_id || generateEventId("view_category")
        });
    }

    return data?.viewcategory_event_id || generateEventId("view_category");
};

/* ------------------ Purchase ------------------ */

export const onPurchaseEventsFB = (data, productIds) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    let totalValue = parseFloat(
        (data?.order_total || "0").replace("$", "").replace(/,/g, "")
    );

    safeFbq("track", "PageView", {
    ...baseUserParams(user),
    customer_Ids: user?.customer_id ? [user.customer_id] : [],
    membership_type: user ? "Yes" : "Guest user",
    total_order: user ? user.total_order : 0,
    value: totalValue,
    currency: "USD",
    event_id: data.order_number ? "event.id.pageview.purchase." + data.order_number : ""
    });


    safeFbq("track", "Purchase", {
        ...baseUserParams(user),
        content_ids: productIds,
        content_type: "product",
        value: totalValue,
        currency: "USD",
        transaction_id: data.order_number,
        event_id: data?.order_number ? "event.id.purchase." + data.order_number : generateEventId("purchase")
    });

        // adroll code
    try {
        window.adroll.track("pageView", {
            "order_id": data?.order_number,
            "conversion_value": parseInt(totalValue),
            "currency": "USD"
        });
        window.__adroll.record_user(window.adroll_custom_data = { "payment_type": data?.payment_mode || "" });
    } catch (e) {
        // adroll not loaded
    }

    return data?.order_number ? "event.id.purchase." + data.order_number : generateEventId("purchase");
};

/* ------------------ Common Page ------------------ */

export const commonPageEventsFB = (page_type) => {

    let user = localStorage.getItem(USER_KEY);
    user = user ? JSON.parse(user) : null;

    const eventId = generateEventId("page_view");

    const pageParams = {
        ...baseUserParams(user),
        page_type: page_type,
        membership_type: user ? "Yes" : "Guest",
        total_order: user?.total_order || "",
        event_id: eventId
    };

    // Standard PageView event
    safeFbq("track", "PageView", pageParams);

    return eventId;
};